import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { MapPin, Users, BedDouble, Bath, Wifi, Snowflake, Car, Utensils, Waves, Tv, WashingMachine, Briefcase, Coffee, Dumbbell, Building, Shield, MessageCircle, Star, Clock, ShieldCheck } from "lucide-react";

const AMENITY_ICONS = {
  wifi: [Wifi, "Wi-Fi"], ac: [Snowflake, "Climatisation"], kitchen: [Utensils, "Cuisine"],
  pool: [Waves, "Piscine"], parking: [Car, "Parking"], tv: [Tv, "TV"],
  washer: [WashingMachine, "Lave-linge"], workspace: [Briefcase, "Bureau"],
  breakfast: [Coffee, "Petit-déjeuner"], gym: [Dumbbell, "Salle de sport"],
  elevator: [Building, "Ascenseur"], security: [Shield, "Sécurité"],
};

const CATEGORY_LABELS = {
  apartment: "Appartement", villa: "Villa", house: "Maison",
  studio: "Studio", hotel_room: "Chambre d'hôtel",
};

export default function LodgingDetail() {
  const { id } = useParams();
  const { fmt } = useCurrency();
  const [l, setL] = useState(null);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    api.get(`/lodgings/${id}`).then((r) => setL(r.data));
  }, [id]);

  if (!l) return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <SiteHeader />
      <div className="max-w-6xl mx-auto px-4 py-24 text-center text-gray-400">Chargement…</div>
      <SiteFooter />
    </div>
  );

  const photos = l.photos?.length ? l.photos : ["https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1400"];
  const contactUrl = l.agency?.email ? `mailto:${l.agency.email}?subject=Demande%20de%20réservation%20-%20${encodeURIComponent(l.name)}` : "";
  const whatsappUrl = l.agency?.phone ? `https://wa.me/${l.agency.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Bonjour, je suis intéressé(e) par le logement "${l.name}" sur MOBIKAR.`)}` : "";

  return (
    <div className="min-h-screen bg-[#f8f9fa]" data-testid="lodging-detail">
      <SiteHeader />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <Link to="/lodgings" className="text-sm text-[#0e7a3f] font-semibold hover:underline">← Retour aux logements</Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2 aspect-[4/3] bg-gray-100 rounded-2xl overflow-hidden">
            <img src={photos[activePhoto]} alt={l.name} className="w-full h-full object-cover" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-1 gap-3">
            {photos.slice(1, 5).map((p, i) => (
              <button key={i} onClick={() => setActivePhoto(i + 1)} className="aspect-[4/3] bg-gray-100 rounded-2xl overflow-hidden">
                <img src={p} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl p-6 md:p-8 border border-gray-100 mbk-card-shadow">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#0e7a3f]/10 text-[#0e7a3f]">{CATEGORY_LABELS[l.category] || l.category}</span>
                  <h1 className="font-display text-3xl md:text-4xl font-black text-[#0a1e3f] mt-2">{l.name}</h1>
                  <div className="text-sm text-gray-500 mt-1 flex items-center gap-1"><MapPin className="h-4 w-4" /> {l.address ? `${l.address}, ` : ""}{l.city}, {l.country}</div>
                </div>
                {l.instant_booking && <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#d4a437]/15 text-[#0a1e3f]">Réservation instantanée</span>}
              </div>

              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <Info icon={Users} label={`${l.max_guests} voyageurs`} />
                <Info icon={BedDouble} label={`${l.bedrooms} chambres · ${l.beds} lits`} />
                <Info icon={Bath} label={`${l.bathrooms} SDB`} />
                <Info icon={Clock} label={`${l.min_nights} nuit${l.min_nights > 1 ? "s" : ""} min.`} />
              </div>

              {l.description && (
                <div className="mt-6 border-t border-gray-100 pt-6">
                  <h3 className="font-display text-lg font-bold text-[#0a1e3f] mb-2">À propos de ce logement</h3>
                  <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{l.description}</p>
                </div>
              )}

              {l.amenities?.length > 0 && (
                <div className="mt-6 border-t border-gray-100 pt-6">
                  <h3 className="font-display text-lg font-bold text-[#0a1e3f] mb-4">Équipements</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {l.amenities.map((a) => {
                      const [Icon, label] = AMENITY_ICONS[a] || [Shield, a];
                      return (
                        <div key={a} className="flex items-center gap-2 text-sm text-gray-700" data-testid={`amenity-icon-${a}`}>
                          <Icon className="h-4 w-4 text-[#0e7a3f]" /> {label}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-6 border-t border-gray-100 pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="text-sm text-gray-700">
                  <div className="font-semibold text-[#0a1e3f]">Arrivée</div>
                  <div>À partir de {l.check_in_time}</div>
                </div>
                <div className="text-sm text-gray-700">
                  <div className="font-semibold text-[#0a1e3f]">Départ</div>
                  <div>Avant {l.check_out_time}</div>
                </div>
              </div>

              {l.house_rules && (
                <div className="mt-6 border-t border-gray-100 pt-6">
                  <h3 className="font-display text-lg font-bold text-[#0a1e3f] mb-2">Règlement intérieur</h3>
                  <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{l.house_rules}</p>
                </div>
              )}
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="bg-white rounded-3xl p-6 border border-gray-100 mbk-card-shadow sticky top-24">
              <div className="text-2xl font-display font-black text-[#0a1e3f]">{fmt(l.price_per_night)}<span className="text-sm font-normal text-gray-400"> /nuit</span></div>
              {l.agency && (
                <div className="mt-5 border-t border-gray-100 pt-5">
                  <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-3">Proposé par</div>
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-[#0a1e3f] text-[#d4a437] font-black flex items-center justify-center">{l.agency.name?.[0]}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#0a1e3f] truncate">{l.agency.name}</div>
                      <div className="text-xs text-[#0e7a3f] font-semibold inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Agence vérifiée</div>
                      {l.agency.rating > 0 && (
                        <div className="text-xs text-[#0a1e3f] font-bold flex items-center gap-1 mt-0.5">
                          <Star className="h-3 w-3 fill-[#d4a437] text-[#d4a437]" /> {Number(l.agency.rating).toFixed(1)} ({l.agency.reviews_count || 0} avis)
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="mt-5 space-y-2">
                <Link
                  to={`/booking/lodging/${l.id}`}
                  data-testid="lodging-book-now"
                  className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-full mbk-btn-primary text-white font-semibold text-sm"
                >
                  <MessageCircle className="h-4 w-4" /> Réserver ce logement
                </Link>
                {contactUrl && (
                  <a href={contactUrl} data-testid="lodging-contact-email" className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-full bg-white border border-gray-200 text-[#0a1e3f] font-semibold text-sm hover:bg-gray-50">
                    Contacter l'agence par email
                  </a>
                )}
                {whatsappUrl && (
                  <a href={whatsappUrl} target="_blank" rel="noreferrer" data-testid="lodging-contact-wa" className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[#25D366] text-white font-semibold text-sm hover:opacity-90">
                    WhatsApp
                  </a>
                )}
              </div>
              <div className="mt-4 text-[11px] text-gray-500 text-center leading-relaxed">Le contrat de location vous sera envoyé automatiquement par email après validation de votre acompte.</div>
            </div>
          </aside>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

const Info = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 text-gray-700">
    <Icon className="h-5 w-5 text-[#0e7a3f]" /> <span className="font-medium">{label}</span>
  </div>
);
